import React from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Search, Users, X } from 'lucide-react'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { sdk } from '@/lib/sdk'
import { cn } from '@/lib/utils'
import type { ChatConnectionState, ChatPerson } from '@/lib/sdk-official/resources/chat'
import { useChat, CHAT_CONNECTION_EVENT, CHAT_PRESENCE_EVENT } from './ChatProvider'
import { StatusSelect, PresenceDot } from './ChatStatus'
import { lastSeenLabel } from './chatStatus'
import { InfiniteSentinel } from '@/components/ui/infinite-sentinel'
import { SCROLL_CLASSES } from './ChatConversationView'
import { UnreadCount } from './UnreadCount'
import { RoleChip, TEAM_RING_CLASSES, teamRoleOf } from './ChatTeam'
import { displayPersonName } from '@/lib/personName'

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } })?.response?.status

const messageOf = (err: unknown): string | undefined =>
  (err as { response?: { data?: { message?: string } } })?.response?.data?.message

/**
 * F-108 "Pessoas" (David, 2026-10-22): a button next to the person's name in
 * the header opens a floating panel on the right with the community's people
 * and their status; a click opens the chat. Only people who turned on
 * "Disponível para conversar" appear (privacy rule), online in green, offline
 * in grey. A different thing from the conversations panel (the inbox).
 */
export const PeoplePanel = ({ onClose, embedded = false, externalQuery }: { onClose: () => void; embedded?: boolean; externalQuery?: string }) => {
  const { t, i18n } = useTranslation('chat')
  const chat = useChat()
  const available = Boolean(chat.me?.chatAvailable)
  /* "Indisponível" (or the badge turned off): no per-person counts. */
  const showCounts = available && chat.me?.showUnreadBadge !== false
  const [ownQuery, setQuery] = React.useState('')
  /* The desktop dock has one search box for both tabs and passes it in. */
  const query = externalQuery ?? ownQuery
  const [people, setPeople] = React.useState<ChatPerson[]>([])
  const [cursor, setCursor] = React.useState<string | null>(null)
  const [state, setState] = React.useState<'loading' | 'ready' | 'failed' | 'unavailable'>('loading')
  const [loadingMore, setLoadingMore] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  /* A request, acceptance or removal elsewhere (socket) updates the rows. */
  React.useEffect(() => {
    const onConnection = (event: Event) => {
      const detail = (event as CustomEvent<{ profileId?: string; connection?: ChatConnectionState }>).detail || {}
      if (detail.profileId && detail.connection) {
        setPeople((list) => list.map((p) => (p.profile.id === detail.profileId ? { ...p, connection: detail.connection } : p)))
      }
    }
    window.addEventListener(CHAT_CONNECTION_EVENT, onConnection)
    return () => window.removeEventListener(CHAT_CONNECTION_EVENT, onConnection)
  }, [])

  const [totals, setTotals] = React.useState<{ team?: number; members?: number }>({})
  const load = React.useCallback(async (q: string) => {
    setState('loading')
    try {
      const page = await sdk.chat.listPeople({ q: q.trim() || undefined })
      setPeople(page.items.filter((p) => p.profile?.id && p.profile.id !== chat.myId))
      setCursor(page.nextCursor)
      /* Section sizes for this search (the first page carries them); hidden when the API does not say. */
      setTotals({ team: page.teamTotal, members: page.membersTotal })
      setState('ready')
    } catch (err) {
      setState(statusOf(err) === 403 ? 'unavailable' : 'failed')
    }
  }, [chat.myId])

  /* Search waits for a pause in typing. */
  React.useEffect(() => {
    if (!available) {
      setState('unavailable')
      return
    }
    const timer = window.setTimeout(() => load(query), query ? 300 : 0)
    return () => window.clearTimeout(timer)
  }, [available, query, load])

  /*
   * Presence from the socket patches the rows in place (no refetch). Someone not in the list yet
   * (just turned available, or beyond the loaded page) is inserted from the event, which carries
   * `canStart` and `connection` per recipient; without them, one debounced refetch of the first page.
   */
  const peopleRef = React.useRef(people)
  peopleRef.current = people
  const queryRef = React.useRef(query)
  queryRef.current = query
  React.useEffect(() => {
    if (!available) return
    let refetchTimer: number | undefined
    const onPresence = (event: Event) => {
      const detail = (event as CustomEvent<Partial<Pick<ChatPerson, 'canStart' | 'connection'>> & { profileId?: string; online?: boolean; status?: ChatPerson['profile']['status']; lastSeenAt?: string | null; profile?: ChatPerson['profile'] }>).detail || {}
      /* F-184: the status travels with every delta (null offline); a known row takes it too. */
      const status = detail.online ? detail.status ?? detail.profile?.status ?? 'AVAILABLE' : null
      if (!detail.profileId || typeof detail.online !== 'boolean') return
      const inList = peopleRef.current.some((p) => p.profile.id === detail.profileId)
      if (inList) {
        setPeople((list) => list.map((p) => (p.profile.id === detail.profileId
          ? { ...p, online: detail.online as boolean, profile: { ...p.profile, status, ...(detail.online ? {} : { lastSeenAt: detail.lastSeenAt ?? null }) }, ...(detail.canStart !== undefined ? { canStart: detail.canStart } : {}), ...(detail.connection ? { connection: detail.connection } : {}) }
          : p)))
      } else if (detail.online && detail.profile && detail.canStart !== undefined) {
        const q = queryRef.current.trim().toLocaleLowerCase()
        if (q && !(detail.profile.name || '').toLocaleLowerCase().includes(q)) return
        const person: ChatPerson = { profile: { ...detail.profile, status }, online: true, canStart: detail.canStart, connection: detail.connection }
        setPeople((list) => (list.some((p) => p.profile.id === person.profile.id) ? list : [...list, person]))
      } else if (detail.online) {
        window.clearTimeout(refetchTimer)
        refetchTimer = window.setTimeout(() => load(queryRef.current), 1500)
      }
    }
    window.addEventListener(CHAT_PRESENCE_EVENT, onPresence)
    return () => {
      window.clearTimeout(refetchTimer)
      window.removeEventListener(CHAT_PRESENCE_EVENT, onPresence)
    }
  }, [available, load])

  const loadMore = async () => {
    if (!cursor || loadingMore) return
    setLoadingMore(true)
    try {
      const page = await sdk.chat.listPeople({ q: query.trim() || undefined, cursor })
      setPeople((list) => [...list, ...page.items.filter((p) => p.profile?.id && p.profile.id !== chat.myId && !list.some((x) => x.profile.id === p.profile.id))])
      setCursor(page.nextCursor)
    } catch {
      setError(t('people.loadError'))
    } finally {
      setLoadingMore(false)
    }
  }



  const open = async (person: ChatPerson) => {
    setError(null)
    try {
      await chat.openWith(person.profile.id, { ...person.profile, online: person.online }, person.connection)
      if (window.innerWidth >= 1024 && chat.panelState === 'COLLAPSED') chat.setPanelState('EXPANDED')
      onClose()
    } catch (err) {
      setError(messageOf(err) || t('errors.cannotMessage'))
    }
  }

  /* Rows follow presence.delta; the live online list covers what changed before the rows loaded. */
  const isOnline = (person: ChatPerson) => person.online || chat.isOnline(person.profile.id)
  const isTeam = (person: ChatPerson) => Boolean(teamRoleOf(person.profile))
  const sorted = [...people].sort((a, b) => Number(isTeam(b)) - Number(isTeam(a)) || Number(isOnline(b)) - Number(isOnline(a)) || (a.profile.name || '').localeCompare(b.profile.name || ''))
  const hasTeam = sorted.some(isTeam)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {!embedded && (
        <div className="flex h-14 shrink-0 items-center gap-2 border-b border-gray-200 px-3">
          <Users className="h-4 w-4 text-gray-500" />
          <span className="text-base font-semibold text-gray-900">{t('people.title')}</span>
          <StatusSelect className="ml-1" />
          <Button variant="ghost" size="icon" className="ml-auto h-9 w-9" onClick={onClose} aria-label={t('close')}>
            <X className="h-5 w-5" />
          </Button>
        </div>
      )}

      {!available ? (
        <p className="px-4 py-6 text-center text-sm text-gray-500" data-chat-people-unavailable>{t('people.unavailable')}</p>
      ) : externalQuery !== undefined ? null : (
        <div className="border-b border-gray-200 p-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('people.search')}
              aria-label={t('people.search')}
              className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-3 text-base focus:border-primary focus:outline-none sm:text-sm"
            />
          </div>
          <p className="mt-2 text-xs text-gray-500">{t('people.onlyAvailable')}</p>
        </div>
      )}

      {error && <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}

      <div className={cn('min-h-0 flex-1 overflow-y-auto', SCROLL_CLASSES)} data-chat-people-list>
        {available && state === 'loading' && people.length === 0 && [0, 1, 2, 3].map((i) => (
          <div key={i} className="flex items-center gap-2.5 px-3 py-1.5" aria-hidden>
            <div className="h-8 w-8 animate-pulse rounded-full bg-gray-100" />
            <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
          </div>
        ))}
        {available && state === 'failed' && (
          <div className="flex flex-col items-center gap-2 px-6 py-10 text-center">
            <AlertTriangle className="h-6 w-6 text-amber-500" />
            <p className="text-sm text-gray-600">{t('people.loadError')}</p>
            <Button size="sm" variant="outline" onClick={() => load(query)}>{t('link.retry')}</Button>
          </div>
        )}
        {available && state === 'ready' && sorted.length === 0 && (
          <div className="flex flex-col items-center px-6 py-10 text-center">
            <Users className="mb-2 h-8 w-8 text-gray-300" />
            <p className="text-sm text-gray-600">{query.trim() ? t('people.noMatch') : t('people.empty')}</p>
          </div>
        )}
        {available && sorted.map((person, index) => {
          const name = displayPersonName(t, person.profile.name)
          const role = teamRoleOf(person.profile)
          /* The team comes first; a small header above it and above the rest, when both are there. */
          const header = hasTeam && (index === 0 || (!role && teamRoleOf(sorted[index - 1].profile)))
            ? [role ? t('people.teamHeader') : t('people.membersHeader'), role ? totals.team : totals.members]
            : null
          const online = isOnline(person)
          /* F-184: the row's own status (patched by presence.delta), else the provider's online list. */
          const status = person.profile.status ?? chat.online.find((p) => p.id === person.profile.id)?.status ?? null
          const busy = online && status === 'BUSY'
          const away = online && status === 'AWAY'
          const unread = showCounts ? chat.conversations.find((c) => c.other?.id === person.profile.id)?.unreadCount || 0 : 0
          /* David: the row is just the person and the status; a click opens the chat window, which holds the actions. */
          return (
            <React.Fragment key={person.profile.id}>
            {header && (
              <p className="px-3 pb-0.5 pt-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500" data-chat-people-header>
                {header[0]}
                {typeof header[1] === 'number' && <span className="font-normal text-gray-400" data-chat-people-header-count> · {header[1]}</span>}
              </p>
            )}
            <button
              type="button"
              onClick={() => open(person)}
              data-chat-person
              className="flex min-h-[48px] w-full items-center gap-2.5 px-3 py-1.5 text-left hover:bg-gray-50"
            >
              <span className="relative shrink-0">
                <Avatar src={person.profile.avatar} alt={name} fallback={person.profile.name} size="sm" className={cn(role && TEAM_RING_CLASSES)} />
                <PresenceDot always online={online} status={status} className="-bottom-px -right-px h-2.5 w-2.5" />
              </span>
              <span className="min-w-0 flex-1">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className={cn('truncate text-sm text-gray-900', unread ? 'font-semibold' : 'font-medium')}>{name}</span>
                  <RoleChip role={role} />
                </span>
                <span className={cn('block truncate text-xs', busy ? 'text-red-700' : away ? 'text-amber-700' : online ? 'text-green-700' : 'text-gray-400')} data-chat-person-status>
                  {busy ? t('people.busy') : away ? t('people.away') : online ? t('people.online') : (chat.me?.chatShowLastSeen === false ? null : lastSeenLabel(t, i18n.language, person.profile.lastSeenAt)) || t('people.offline')}
                  {(person.profile.jobTitle || person.profile.tagline) && <span className="text-gray-500"> · {person.profile.jobTitle || person.profile.tagline}</span>}
                </span>
              </span>
              <UnreadCount count={unread} />
            </button>
            </React.Fragment>
          )
        })}
        {available && <InfiniteSentinel hasMore={Boolean(cursor) && !error} loading={loadingMore} onLoadMore={loadMore} count={people.length} />}
        {available && chat.me?.canStart === false && (
          <p className="px-3 py-2 text-xs text-gray-500">{chat.me?.cannotStartReason === 'paidOnly' ? t('online.paidOnly') : t('online.teamOnly')}</p>
        )}
      </div>
    </div>
  )
}
